import { useMemo, useState } from "react";
import { ComponentsHelper } from "../../../core/services/ComponentHelper";
import IconTextButton from "../../../repertoire/components/iconTextButton/IconTextButton";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { parseDateType } from "../../../membersportal/util/dateHelpers";

interface IReadOnlyDivTableProps {
	fields: IRepertoireField[];
	dataSource: any[];
	titleFieldName?: string;
	emptyText?: string;
	customerDatePreference?: string;
}

const ReadOnlyDivHeader = ({
	title,
	isOpen,
	onToggle,
}: {
	title: React.ReactNode;
	isOpen: boolean;
	onToggle: () => void;
}) => {
	const iconClass = isOpen
		? "icon ms-Icon ms-Icon--ChevronUp"
		: "icon ms-Icon ms-Icon--ChevronDown";

	return (
		<div className="dataDivHeader">
			<div className="dataDivInfoHeader">{title}</div>

			<div onClick={onToggle}>
				<span style={{ pointerEvents: "none" }}>
					<IconTextButton icon={iconClass} disabled={false} />
				</span>
			</div>
		</div>
	);
};

const ReadOnlyDivContent = ({
	row,
	fields,
}: {
	row: any;
	fields: IRepertoireField[];
}) => (
	<div className="dataDivContent borderTop">
		{fields.map((field) => (
			<div className="dataDivContentRow" key={field.name}>
				<span className="dataDivLabel">{field.data}:</span>
				<span className="dataDivValue">{row[field.name]}</span>
			</div>
		))}
	</div>
);

export const ReadOnlyDivTable = ({
	fields,
	dataSource,
	titleFieldName,
	emptyText,
	customerDatePreference,
}: IReadOnlyDivTableProps) => {
	const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

	const orderedFields = useMemo(() => {
		const ordered = ComponentsHelper.orderFieldsBySpecificField(
			fields,
			"order"
		);
		return ordered?.length ? ordered : fields;
	}, [fields]);

	const contentFields = useMemo(
		() => orderedFields.filter((field) => field.name !== titleFieldName),
		[orderedFields, titleFieldName]
	);

	const toggleRow = (key: string) => {
		setOpenRows((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const data = useMemo(() => {
		return dataSource?.map((row) => ({
			key: row?.applicationId,
			applicationId: row?.applicationId,
			ticketNumber: row?.ticketNumber,
			title: row?.title,
			caseType: row?.caseType,
			caseOrigin: row?.caseOrigin,
			contact: row?.name,
			submittedDateTime:
				row?.submittedDateTime &&
				parseDateType(
					row?.submittedDateTime,
					customerDatePreference + " HH:mm:ss",
				),
			lastModifiedDateTime:
				row?.lastModifiedDate &&
				parseDateType(
					row?.lastModifiedDate,
					customerDatePreference + " HH:mm:ss",
				),
			status: row?.status,
		}));
	}, [dataSource]);

	return (
		<div className="dataDivTable">
			{data?.length > 0 ? (
				data.map((row, index) => {
					const key = row.key ?? `${index}`;
					const isOpen = !!openRows[key];

					return (
						<div
							className={`dataDivRow ${!isOpen ? "borderBottom" : ""}`}
							key={key}
						>
							<ReadOnlyDivHeader
								title={row[titleFieldName]}
								isOpen={isOpen}
								onToggle={() => toggleRow(key)}
							/>

							{isOpen && (
								<ReadOnlyDivContent
									row={row}
									fields={contentFields}
								/>
							)}
						</div>
					);
				})
			) : (
				<div className="noData">{emptyText}</div>
			)}
		</div>
	);
};
